import { reactive, watch } from 'vue'
import type { NavDisplayMode, ThemePreference } from './types'

export interface Preferences {
  theme: ThemePreference
  navDisplay: NavDisplayMode
  detailed: boolean
  reducedMotion: boolean
  shortcuts: boolean
  tips: boolean
  volume: number
}

function parseBoolean(key: string, fallback: boolean): boolean {
  const value = localStorage.getItem(key)
  return value === null ? fallback : value === 'true'
}

function parseVolume(): number {
  const value = Number(localStorage.getItem('ci.volume') ?? '0.55')
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.55
}

function initialTheme(): ThemePreference {
  const value = localStorage.getItem('ci.theme')
  return value === 'light' || value === 'dark' || value === 'system'
    ? value
    : 'system'
}

function initialNav(): NavDisplayMode {
  const value = localStorage.getItem('ci.nav-display')
  return value === 'icons-text' ||
    value === 'icons-only' ||
    value === 'text-only' ||
    value === 'collapsed'
    ? value
    : 'icons-text'
}

export const preferences = reactive<Preferences>({
  theme: initialTheme(),
  navDisplay: initialNav(),
  detailed: parseBoolean('ci.detailed', false),
  reducedMotion: parseBoolean(
    'ci.reduced-motion',
    window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  ),
  shortcuts: parseBoolean('ci.shortcuts', true),
  tips: parseBoolean('ci.tips', true),
  volume: parseVolume(),
})

function resolveTheme(): 'light' | 'dark' {
  if (preferences.theme === 'light' || preferences.theme === 'dark')
    return preferences.theme
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

export function applyPreferences(): void {
  document.documentElement.dataset.theme = resolveTheme()
  document.documentElement.dataset.motion = preferences.reducedMotion
    ? 'reduced'
    : 'full'
}

watch(
  preferences,
  () => {
    localStorage.setItem('ci.theme', preferences.theme)
    localStorage.setItem('ci.nav-display', preferences.navDisplay)
    localStorage.setItem('ci.detailed', String(preferences.detailed))
    localStorage.setItem('ci.reduced-motion', String(preferences.reducedMotion))
    localStorage.setItem('ci.shortcuts', String(preferences.shortcuts))
    localStorage.setItem('ci.tips', String(preferences.tips))
    localStorage.setItem('ci.volume', String(preferences.volume))
    applyPreferences()
  },
  { deep: true },
)

window
  .matchMedia('(prefers-color-scheme: dark)')
  .addEventListener('change', applyPreferences)
